// ==================== CUSTOMER DASHBOARD PAGE ====================

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { staggerContainer } from '../../utils/animations';
import { useAuth } from '../../contexts';
import { 
  customerDashboardService,
  type CustomerDashboardData,
  type DateRange,
} from '../../services/customer-dashboard.service';

// Import dashboard components
import {
  StatsGrid,
  QuickActions,
  MessageAnalytics,
  RecentCampaigns,
  RecentActivity,
  TemplateStatus,
  WhatsAppStatus,
  SubscriptionUsage,
  // New analytics components
  EngagementMetrics,
  TopCampaigns,
  KeywordBotStats,
  ContactGrowth,
  SmsAccountStatus,
  SmsMessageAnalytics,
  SmsAnalytics,
  SmsEngagement,
} from './components';

import { REFRESH_INTERVALS } from './constants';

// ==================== MAIN COMPONENT ====================

const CustomerDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<CustomerDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('7d');

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const data = await customerDashboardService.getDashboardData(dateRange, 5);
      setDashboardData(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(message);
      console.error('Dashboard fetch error:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [dateRange]);

  // Initial load and auto-refresh
  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh every minute
    const interval = setInterval(() => {
      fetchDashboardData(true);
    }, REFRESH_INTERVALS.DASHBOARD);

    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Handle date range change
  const handleDateRangeChange = useCallback((newRange: DateRange) => {
    setDateRange(newRange);
  }, []);

  // Manual refresh
  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">
            {getGreeting()}, {user?.firstName || 'there'}!
          </h1>
          <p className="text-(--color-muted-gray) mt-0.5 text-sm">
            Here's an overview of your messaging performance.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="self-start sm:self-auto"
        >
          <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30"
        >
          <AlertCircle size={18} className="text-red-500 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-500">Failed to load dashboard</p>
            <p className="text-xs text-red-400">{error}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => fetchDashboardData()}>
            Retry
          </Button>
        </motion.div>
      )}

      {/* Main Dashboard Content */}
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="space-y-4"
      >
        {/* Stats Grid */}
        <StatsGrid 
          stats={dashboardData?.stats || null} 
          isLoading={isLoading} 
        />

        {/* Main Content Grid - 3 Columns */}
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Left Column - Analytics & Campaigns (Spans 2 cols) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Message Analytics (WhatsApp) */}
            <MessageAnalytics
              data={dashboardData?.messageAnalytics || null}
              isLoading={isLoading}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
            />

            {/* SMS Message Analytics */}
            <SmsMessageAnalytics
              data={dashboardData?.smsAnalytics || null}
              isLoading={isLoading}
            />
            
            {/* WhatsApp & SMS Engagement Row */}
            <div className="grid md:grid-cols-2 gap-4">
              <EngagementMetrics
                data={dashboardData?.engagementMetrics || null}
                isLoading={isLoading}
              />
              <SmsEngagement
                data={dashboardData?.smsAnalytics || null}
                isLoading={isLoading}
              />
            </div>

            {/* Contact Growth */}
            <ContactGrowth
              data={dashboardData?.contactGrowth || null}
              isLoading={isLoading}
            />

            {/* Recent Campaigns */}
            <RecentCampaigns
              campaigns={dashboardData?.recentCampaigns || []}
              isLoading={isLoading}
            />

            {/* Activity & Templates Row */}
            <div className="grid md:grid-cols-2 gap-4">
              <RecentActivity
                activities={dashboardData?.recentActivity || []}
                isLoading={isLoading}
              />
              <TemplateStatus
                templates={dashboardData?.templateStatus || []}
                isLoading={isLoading}
              />
            </div>
          </div>

          {/* Right Column - Sidebar Cards */}
          <div className="space-y-4">
            {/* WhatsApp Status */}
            <WhatsAppStatus
              stats={dashboardData?.stats || null}
              isLoading={isLoading}
            />

            {/* SMS Account Status */}
            <SmsAccountStatus
              stats={dashboardData?.stats || null}
              isLoading={isLoading}
            />

            {/* SMS Analytics Quick Stats */}
            <SmsAnalytics
              data={dashboardData?.smsAnalytics || null}
              isLoading={isLoading}
            />

            {/* Top Performing Campaigns */}
            <TopCampaigns
              campaigns={dashboardData?.topCampaigns || []}
              isLoading={isLoading}
            />

            {/* Keyword Bot Stats */}
            <KeywordBotStats
              data={dashboardData?.keywordBotAnalytics || null}
              isLoading={isLoading}
            />

            {/* Subscription Usage */}
            <SubscriptionUsage
              stats={dashboardData?.stats || null}
              isLoading={isLoading}
            />

            {/* Quick Actions */}
            <QuickActions />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export { CustomerDashboard };
